import {
  AnalyticsEvent,
  ButtonComponentV2,
  Maybe,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import DepositStatusTag from 'features/directDeposit/components/DepositStatusTag';
import DirectDepositChecklist from 'features/directDeposit/components/DirectDepositChecklist';
import DirectDepositHistoryTransactions from 'features/directDeposit/components/DirectDepositHistoryTransactions';
import PayrollProviderLinkAlert from 'features/directDeposit/components/PayrollProviderLinkAlert';
import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  parseGQLAnalyticsEvent,
  parseOptionalGQLAnalyticsEvent,
} from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { PINWHEEL_LAUNCH_DEEP_LINK } from 'shared/constants/pinWheel';
import { redirectToHomePage } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  useViewDidFocusLogScreenAnalytics,
  useViewDidFocusScreenAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const DirectDepositManagement = () => {
  const router = useRouter();
  const { name } = useLocalSearchParams<{
    name?: string;
  }>();

  const { bottom } = useSafeAreaInsets();

  const [isPinwheelDeepLinkInitiated, setIsPinwheelDeepLinkInitiated] =
    useState(false);

  const {
    ddManagementPage,
    isLoading,
    ddManagementErrorPage,
    pinwheelFlowPageError,
    shouldLaunchPinwheelSDK,
    fetchPinWheelFlowPageData,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({
    requestPolicy: 'cache-first',
  });

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { segmentClient } = useAnalyticsConfiguration();

  useViewDidFocusScreenAnalyticsEvent(
    pinwheelFlowPageError
      ? {
          key: 'ddManagementError',
        }
      : null,
  );

  const analytics = parseOptionalGQLAnalyticsEvent(
    ddManagementPage?.displayAnalytics
      ? ddManagementPage.displayAnalytics
      : null,
  );

  useViewDidFocusLogScreenAnalytics(analytics);

  const errorScreenButton = {
    secondaryButton: {
      title: 'Back',
      onPress: () => router.back(),
    },
  };

  const logAnalytics = (analyticsEvent: Maybe<AnalyticsEvent> | undefined) => {
    if (!analyticsEvent) {
      return;
    }
    const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
    void segmentClient.logEvent(name, properties);
  };

  const refetch = useCallback(
    () => fetchPinWheelFlowPageData('network-only'),
    [fetchPinWheelFlowPageData],
  );

  const handlePress = (action: ButtonComponentV2) => {
    if (
      action?.buttonOnClick?.__typename === Typenames.LaunchPinwheelFlowAction
    ) {
      logAnalytics(action.buttonOnClick.analyticsEvent);
      initiatePinwheel({
        onOutcome: ({ hasPinwheelErrorEvent, success }) => {
          if (success || hasPinwheelErrorEvent) {
            void refetch();
          }
        },
        onLinkTokenError: handleDdManagementLinkTokenFailure,
      });
    } else if (
      action?.buttonOnClick?.__typename === Typenames.RouteNavigationAction &&
      action.buttonOnClick.route
    ) {
      logAnalytics(action.buttonOnClick.analyticsEvent);
      router.push(action.buttonOnClick.route as Href);
    }
  };

  useEffect(() => {
    if (
      (shouldLaunchPinwheelSDK || name === PINWHEEL_LAUNCH_DEEP_LINK) &&
      !isPinwheelDeepLinkInitiated
    ) {
      setIsPinwheelDeepLinkInitiated(true);
      initiatePinwheel({
        onOutcome: ({ success, hasPinwheelErrorEvent }) => {
          if (success || hasPinwheelErrorEvent) {
            void refetch();
          } else {
            redirectToHomePage();
          }
        },
      });
    }
  }, [
    initiatePinwheel,
    isPinwheelDeepLinkInitiated,
    name,
    refetch,
    shouldLaunchPinwheelSDK,
  ]);

  return (
    <ScrollView
      contentContainerStyle={[styles.scrollContent, { paddingBottom: bottom }]}
      style={styles.container}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      {ddManagementPage?.inlineAlerts?.length ? (
        <View style={styles.alertsWrapper}>
          {ddManagementPage.inlineAlerts.map((alert, index) => (
            <View
              key={alert.id}
              style={
                index !== ddManagementPage.inlineAlerts.length - 1 &&
                styles.alertSpacing
              }
            >
              <PayrollProviderLinkAlert alert={alert} onDismissed={refetch} />
            </View>
          ))}
        </View>
      ) : null}

      {ddManagementPage?.statusTag?.text ? (
        <DepositStatusTag statusTag={ddManagementPage.statusTag} />
      ) : null}

      {ddManagementPage?.title ? (
        <View style={styles.titleText}>
          <TitleLgText>{ddManagementPage.title}</TitleLgText>
        </View>
      ) : null}

      {ddManagementPage?.subTitle ? (
        <View style={styles.subTitleText}>
          <BodyBaseText>{ddManagementPage.subTitle}</BodyBaseText>
        </View>
      ) : null}

      {ddManagementPage?.stepChecklist?.length ? (
        <DirectDepositChecklist steps={ddManagementPage.stepChecklist} />
      ) : null}

      {ddManagementPage?.actions?.length ? (
        <View style={styles.footer}>
          {ddManagementPage.actions.map((action, index) => {
            return (
              <View
                key={action.id}
                style={index > 0 ? styles.button : undefined}
              >
                <Button
                  label={action.buttonText}
                  type={action.buttonStyle}
                  onPress={() => handlePress(action)}
                />
              </View>
            );
          })}
        </View>
      ) : null}

      {ddManagementPage?.byMonthsHistory ? (
        <DirectDepositHistoryTransactions
          transactionsData={ddManagementPage.byMonthsHistory}
          footerButton={ddManagementPage.footerButton}
        />
      ) : null}

      <ScreenTransitionOverlay
        isLoading={isLoading || isPinwheelLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          pinwheelFlowPageError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={ddManagementErrorPage?.title ?? defaultTitle}
              messageBody={ddManagementErrorPage?.message ?? defaultMessage}
              {...errorScreenButton}
            />
          ) : null
        }
      />
    </ScrollView>
  );
};

export default DirectDepositManagement;

const styles = StyleSheet.create({
  alertSpacing: {
    marginBottom: theme.spacing.s,
  },
  alertsWrapper: {
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
  },
  button: {
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  footer: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.s,
  },
  scrollContent: {
    flexGrow: 1,
  },
  subTitleText: {
    marginHorizontal: theme.spacing.m,
    marginVertical: theme.spacing.m,
  },
  titleText: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
});
